import { useState, useMemo } from 'react';
import { Typography, Stack, Switch, Avatar, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import DashboardWidget from '@/components/user-dashboard/common/dashboard-widget';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import ContributorProfileModal from './contributor-profile.modal';
import ProfileImagePreview from './profile-image-preview';
import FlowPlaceholder from '@/components/flows/detail/edit-details/flow-placeholder';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useTranslations } from '@/i18n/use-translations';
import { ProfilePhotoAvatar } from '@/components/common/profile-photo-avatar';
import { useGetContributorProfilePhotoUrl } from '@/state/queries/contributors/use-get-contributor-profile-photo-url';

const ProfileWidget = ({ contributor }: { contributor: ContributorSummaryDto }) => {
  const t = useTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });

  const contributorProfile = useMemo(() => contributor.profiles?.[contributor.profiles.length - 1], [contributor]);

  const userContributorProfile = contributor.userContributor?.providedProfile;
  const hasUserInput = !!userContributorProfile;

  // Default to showing the profile that was updated most recently
  const [useUserInput, setUseUserInput] = useState(() => {
    if (!hasUserInput) return false;

    // ProfileDto doesn't have updatedAt, so we can only check userContributorProfile
    const userContributorUpdatedAt = userContributorProfile?.updatedAt;

    // If userContributorProfile has updatedAt, prefer showing user input
    // Otherwise default to contributor profile (on event)
    return !!userContributorUpdatedAt;
  });

  // Determine which profile to display
  const displayProfile = useUserInput && hasUserInput ? userContributorProfile : contributorProfile;

  const displayName = displayProfile?.displayName || '';
  const role = displayProfile?.title || '';
  const organization = displayProfile?.organization || '';

  // UserContributor uses photoUri, Contributor uses assetName
  const contributorAssetName = contributorProfile?.assetName;
  // Legacy support: check if photoUri exists on contributorProfile (may not be in type definition)
  const contributorPhotoUri =
    (contributorProfile && 'photoUri' in contributorProfile && typeof contributorProfile.photoUri === 'string'
      ? contributorProfile.photoUri
      : '') || '';

  // Get download URL for Contributor profile photo (if using assetName)
  // Hook must be called unconditionally, but will only fetch when both contributorId and assetName are provided
  const { data: contributorPhotoUrl } = useGetContributorProfilePhotoUrl(
    contributorAssetName ? contributor.id : undefined,
    contributorAssetName,
  );

  // Determine photo URI/URL based on which profile is displayed
  const photoUri = useMemo(() => {
    if (userContributorProfile && displayProfile === userContributorProfile) {
      return userContributorProfile.photoUri || '';
    }
    // For contributor profile, use the fetched URL if assetName exists, otherwise fall back to legacy photoUri
    if (displayProfile === contributorProfile) {
      return contributorPhotoUrl || contributorPhotoUri || '';
    }
    return '';
  }, [userContributorProfile, displayProfile, contributorProfile, contributorPhotoUrl, contributorPhotoUri]);

  const bio = displayProfile?.bio || '';

  if (!contributor.acceptedAt) {
    return (
      <DashboardWidget title="My Profile">
        <Typography variant="body2" color="text.secondary">
          Confirm your participation to complete your profile.
        </Typography>
      </DashboardWidget>
    );
  }

  return (
    <>
      <DashboardWidget
        title="My Profile"
        subtitle={t('myEventsList.contributorProfileForm.description')}
        headerButtons={[
          {
            icon: <EditIcon />,
            label: 'PROFILE',
            variant: 'button',
            onClick: () => setIsModalOpen(true),
          },
        ]}
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            width: '100%',
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Switch to toggle between stored and user input */}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ width: '100%' }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              on event
            </Typography>
            <Switch
              checked={useUserInput}
              onChange={(e) => setUseUserInput(e.target.checked)}
              disabled={!hasUserInput}
              size="medium"
            />
            <Typography
              variant="body1"
              color={hasUserInput ? 'text.secondary' : 'text.disabled'}
              sx={{ fontWeight: 500 }}
            >
              my input
            </Typography>
          </Stack>

          {/* Profile content */}
          <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
            {/* Picture placeholder */}
            {contributorAssetName && displayProfile === contributorProfile ? (
              <ProfilePhotoAvatar
                contributorId={contributor.id}
                assetName={contributorAssetName}
                displayName={displayName}
                onClick={() => photoUri && setIsPreviewOpen(true)}
                sx={{
                  'width': { xs: 72, sm: 90 },
                  'height': { xs: 96, sm: 120 },
                  'flexShrink': 0,
                  'bgcolor': 'primary.main',
                  'borderRadius': 1,
                  'cursor': photoUri ? 'pointer' : 'default',
                  '& img': {
                    objectFit: 'cover',
                  },
                }}
              />
            ) : (
              <Avatar
                key={photoUri}
                src={photoUri}
                alt={displayName || 'Profile'}
                onClick={() => photoUri && setIsPreviewOpen(true)}
                sx={{
                  'width': { xs: 72, sm: 90 },
                  'height': { xs: 96, sm: 120 },
                  'flexShrink': 0,
                  'bgcolor': 'primary.main',
                  'borderRadius': 1,
                  'cursor': photoUri ? 'pointer' : 'default',
                  '& img': {
                    objectFit: 'cover',
                  },
                }}
              >
                {displayName ? displayName.charAt(0).toUpperCase() : '?'}
              </Avatar>
            )}

            {/* Profile information */}
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              {displayName && (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {displayName}
                </Typography>
              )}
              {role && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {role}
                </Typography>
              )}
              {organization && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {organization}
                </Typography>
              )}
            </Stack>
          </Stack>

          {/* Bio preview / empty placeholder */}
          {bio ? (
            <Box
              sx={{
                height: isWideScreen ? 'calc(0.875rem * 1.5 * 6)' : 'calc(0.875rem * 1.5 * 3)',
                minHeight: 0,
                flexShrink: 0,
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: isWideScreen ? 6 : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  m: 0,
                }}
              >
                {bio}
              </Typography>
            </Box>
          ) : (
            <FlowPlaceholder
              title={t('myEventsList.contributorProfileForm.bioEmptyTitle')}
              subtitle={t('myEventsList.contributorProfileForm.bioEmptyHint')}
              icon={<RecordVoiceOverOutlinedIcon sx={{ fontSize: 36, color: 'action.disabled' }} />}
            />
          )}
        </Stack>
      </DashboardWidget>

      <ContributorProfileModal open={isModalOpen} onClose={() => setIsModalOpen(false)} contributor={contributor} />

      <ProfileImagePreview
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        imageUrl={
          contributorAssetName && displayProfile === contributorProfile
            ? contributorPhotoUrl || contributorPhotoUri || undefined
            : photoUri || undefined
        }
        alt={displayName || 'Profile'}
      />
    </>
  );
};

export default ProfileWidget;
